import { formatValue } from './formatters';
import { escapeHtml } from '@opengov/reporting-types/src/escapeHtml';
import { AlignValue, SeriesColumnOptions, VerticalAlignValue } from 'highcharts/highcharts';
import { BarLabelPosition, BaseChartProps } from '../types/chartProps';
import { applyBaseChartOptions } from './baseChartUtils';
import { prepareSeriesDataForMultipleYAxis, prepareSeriesDataForSingleYAxis } from './seriesUtils';
import { supportsMultipleYAxis } from './axisUtils';
import { ValueType } from '../types';

//Percentage use to transform label font size
const BASE_TRANSFORM_PERCENTAGE = 20;
const BASE_LABEL_FONT_SIZE = 0.7;
const MAX_LABEL_SIZE = 1.5;

const getBarLabelPosition = (position: BarLabelPosition | undefined, defaultPosition: BarLabelPosition) => {
  return position ?? defaultPosition;
};

export function determineLabelFontSize(percentage: number) {
  const scale = (percentage - BASE_TRANSFORM_PERCENTAGE) * 0.01;

  // Base is 0.7, final fontsize will divide it to avoid getting for label font size smaller than base
  const fontSize = (BASE_LABEL_FONT_SIZE + scale) / BASE_LABEL_FONT_SIZE;

  if (scale > 0) {
    return Math.max(BASE_LABEL_FONT_SIZE, Math.min(MAX_LABEL_SIZE, fontSize));
  }
  return BASE_LABEL_FONT_SIZE;
}

export function getDataLabel(value: string, valuePercentage: number) {
  if (valuePercentage > BASE_TRANSFORM_PERCENTAGE) {
    return `<span class="highcharts-data-label-text" style="font-size: ${determineLabelFontSize(
      valuePercentage
    )}em;">${escapeHtml(value)}</span>`;
  }
  //return formatted value as it is if label does not need to scale up or disabled
  return escapeHtml(value);
}

export function createDataLabelFormatterForStackingNormal(params: BaseChartProps) {
  return function (this: Highcharts.PointLabelObject) {
    const valueDisplayType = this.series.options?.custom?.valueDisplayType as ValueType;
    const isPrimaryYAxis = this.series.options?.custom?.isPrimaryYAxis;
    const valuePercentage = this.percentage;
    const formattedValue = formatValue(params.formatting, this.y ?? 0, valueDisplayType);

    if (isPrimaryYAxis) {
      return getDataLabel(formattedValue, valuePercentage);
    }

    return '';
  };
}

export function createDataLabelFormatterForStackingPercent(params: BaseChartProps) {
  return function (this: Highcharts.PointLabelObject) {
    const isPrimaryYAxis = this.series.options?.custom?.isPrimaryYAxis;
    const valuePercentage = this.percentage;
    const formattedValue = formatValue(params.formatting, this.percentage / 100, 'percentage');

    if (isPrimaryYAxis) {
      return getDataLabel(formattedValue, valuePercentage);
    }

    return '';
  };
}

export function createDefaultDataLabelFormatter(params: BaseChartProps) {
  return function (this: Highcharts.PointLabelObject) {
    const isPrimaryYAxis = this.series.options?.custom?.isPrimaryYAxis;
    if (!isPrimaryYAxis) {
      return '';
    }

    const valueDisplayType = this.series.options?.custom?.valueDisplayType as ValueType;
    const formattedValue = params.isPercentChart
      ? formatValue(params.formatting, this.y ?? 0, 'percentage')
      : formatValue(params.formatting, this.y ?? 0, valueDisplayType);

    let valuePercentage = 0;
    if (params.isPercentChart) {
      valuePercentage = (this.y ?? 0) * 100;
    } else if (this.percentage) {
      valuePercentage = this.percentage;
    } else if (this.point.options.custom?.percentTotal) {
      valuePercentage = this.point.options.custom?.percentTotal * 100;
    } else {
      // For grouped bars (stacking: 'none'), calculate percentage based on max value in the same category
      // This enables font scaling based on bar size relative to other bars in the group
      const currentValue = Math.abs(this.y ?? 0);
      if (currentValue > 0) {
        const chart = this.series?.chart;
        const allSeries = chart?.series;
        if (allSeries) {
          const categoryIndex = (typeof this.x === 'number' ? this.x : 0) as number;
          const totalCategories = chart?.xAxis?.[0]?.categories?.length ?? 0;

          // apply font scaling only when 2 or fewer categories
          if (totalCategories <= 2) {
            let maxValueInCategory = currentValue;
            let barCountInCategory = 0;

            // Find all series and check values in the same category
            for (const series of allSeries) {
              const seriesOptions = series.options;
              if (
                seriesOptions?.custom?.isPrimaryYAxis === isPrimaryYAxis &&
                series.data?.[categoryIndex]
              ) {
                const point = series.data[categoryIndex];
                const pointY = point?.y;
                if (typeof pointY === 'number' && !isNaN(pointY)) {
                  const pointValue = Math.abs(pointY);
                  if (pointValue > 0) {
                    maxValueInCategory = Math.max(maxValueInCategory, pointValue);
                    barCountInCategory++;
                  }
                }
              }
            }

            // If there's only one bar in the category, give it a high percentage for larger font
            // If there are multiple bars, calculate percentage relative to the max
            if (barCountInCategory === 1) {
              valuePercentage = 100;
            } else if (maxValueInCategory > 0) {
              valuePercentage = (currentValue / maxValueInCategory) * 100;
            }
          }
        }
      }
    }

    return getDataLabel(formattedValue, valuePercentage);
  };
}

export function createDataLabelFormatterForMultipleYAxis(params: BaseChartProps) {
  const stackingNormalFormatter = createDataLabelFormatterForStackingNormal(params);
  const stackingPercentFormatter = createDataLabelFormatterForStackingPercent(params);
  const defaultFormatter = createDefaultDataLabelFormatter(params);
  return function (this: Highcharts.PointLabelObject) {
    const stacking = this.series.options?.custom?.stacking;
    if (stacking === 'normal') {
      return stackingNormalFormatter.call(this);
    }
    if (stacking === 'percent') {
      return stackingPercentFormatter.call(this);
    }
    return defaultFormatter.call(this);
  };
}

export function createDataLabelFormatterForSingleYAxis(params: BaseChartProps) {
  if (params.stacking === 'normal') {
    return createDataLabelFormatterForStackingNormal(params);
  } else if (params.stacking === 'percent') {
    return createDataLabelFormatterForStackingPercent(params);
  }
  return createDefaultDataLabelFormatter(params);
}

export function createDataLabelFormatter(params: BaseChartProps) {
  if (supportsMultipleYAxis(params.yAxis)) {
    return createDataLabelFormatterForMultipleYAxis(params);
  }
  return createDataLabelFormatterForSingleYAxis(params);
}

function applyBarChartSeriesData(options: Highcharts.Options, params: BaseChartProps) {
  if (supportsMultipleYAxis(params.yAxis)) {
    options.series = prepareSeriesDataForMultipleYAxis<SeriesColumnOptions>(params, 'column');
  } else {
    options.series = prepareSeriesDataForSingleYAxis<SeriesColumnOptions>(params, 'column');
  }
  return options;
}

function applyBarChartLabels(options: Highcharts.Options, params: BaseChartProps): Highcharts.Options {
  options.plotOptions = options.plotOptions || {};
  options.plotOptions.series = options.plotOptions.series || {};

  const isLabelsEnabled = params.barLabels?.enabled || params.labelsEnabled;

  if (!isLabelsEnabled) {
    options.plotOptions.series.dataLabels = {
      enabled: false,
      inside: false
    };
    return options;
  }

  const isHorizontalBar = options.chart?.type === 'bar';
  const hasCustomPosition = Boolean(params.barLabels?.enabled);

  // Custom position only applies to the axis along the bar length:
  // - Horizontal bars: custom position affects horizontal alignment (align)
  // - Vertical bars: custom position affects vertical alignment (verticalAlign)
  const align: AlignValue =
    hasCustomPosition && isHorizontalBar
      ? (getBarLabelPosition(params.barLabels?.position, 'center') as AlignValue)
      : 'center';

  const verticalAlign: VerticalAlignValue =
    hasCustomPosition && !isHorizontalBar
      ? (getBarLabelPosition(params.barLabels?.position, 'middle') as VerticalAlignValue)
      : 'middle';

  options.plotOptions.series.dataLabels = {
    enabled: true,
    inside: true,
    formatter: createDataLabelFormatter(params),
    style: {
      fontWeight: '600',
      dominantBaseline: isHorizontalBar ? 'middle' : 'unset'
    },
    align,
    verticalAlign
  };

  return options;
}

export function applyBarChartOptions(options: Highcharts.Options, params: BaseChartProps) {
  const fns = [
    applyBaseChartOptions,

    // Bar chart specific
    applyBarChartSeriesData,
    applyBarChartLabels
  ];
  return fns.reduce((acc, fn) => fn(acc, params), options);
}
